import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Phone, Loader2, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { serviceSchema, offerSchema } from "@/utils/schemas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated internal links to build crawl paths (helps "Crawled - currently not indexed")
const RELATED_SERVICES = [
  { name: "Residential Junk Removal", slug: "residential-junk-removal" },
  { name: "Furniture Removal", slug: "furniture-removal" },
  { name: "Appliance Removal", slug: "appliance-removal" },
  { name: "Garage Cleanouts", slug: "garage-cleanouts" },
  { name: "Estate Cleanouts", slug: "estate-cleanouts" },
  { name: "Construction Debris Removal", slug: "construction-debris-removal" },
  { name: "Hoarder Cleanouts", slug: "hoarder-cleanouts" },
  { name: "Light Demolition", slug: "light-demolition" },
];
const RELATED_AREAS = [
  { name: "Fort Worth", slug: "fort-worth-tarrant-junk-removal" },
  { name: "Denton", slug: "denton-denton-junk-removal" },
  { name: "Keller", slug: "keller-tarrant-junk-removal" },
  { name: "Weatherford", slug: "weatherford-parker-junk-removal" },
  { name: "Southlake", slug: "southlake-tarrant-junk-removal" },
  { name: "Frisco", slug: "frisco-denton-junk-removal" },
];

export default function ServiceLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const { formatted_phone, phone_link } = useSiteConfig();

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/services/pages/${slug}`);
        setPage(response.data);
      } catch (err) {
        setError("Service page not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  const servicePageSchemas = useMemo(() => {
    if (!page) return [];
    const title = page.hero_title?.replace(/\*+/g, '') || page.service_name || '';
    return [
      serviceSchema({ name: title, description: page.meta_description || '' }),
      offerSchema("Free, no-obligation quote"),
    ];
  }, [page]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
        <Link to="/services">
          <Button className="bg-[#b51a00] hover:bg-[#8f1500]">View All Services</Button>
        </Link>
      </div>
    );
  }

  const serviceLabel = page.service_type?.replace(/-/g, ' ').toUpperCase() || 'PROFESSIONAL SERVICE';

  return (
    <>
      <SEOHead 
        title={page.hero_title?.replace(/\*+/g, '')}
        description={page.meta_description}
        keywords={(page.keywords || []).join(", ")}
        schemas={servicePageSchemas}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Services", url: "/services" },
          { name: page.hero_title?.replace(/\*+/g, '') || page.service_name },
        ]}
      />
      <div data-testid="service-landing-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                {serviceLabel}
              </span>
              {page.city && (
                <>
                  <span className="text-gray-500">|</span>
                  <span className="text-gray-400 uppercase tracking-widest text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {page.city}, TX
                  </span>
                </>
              )}
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-wide leading-tight mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {(page.hero_title || '').replace(/\*+/g, '')}
            </h1>
            {page.hero_subtitle && (
              <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed max-w-3xl">
                {page.hero_subtitle.replace(/\*+/g, '')}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="hero-cta-btn"
                  className="bg-[#b51a00] hover:bg-[#8f1500] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={phone_link}>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  {formatted_phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      {page.benefits && page.benefits.length > 0 && (
        <section className="bg-[#b51a00] py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
              {page.benefits.slice(0, 6).map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-white">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="font-semibold text-sm md:text-base">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="service-content"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>
      </section>

      {/* FAQs Section */}
      {page.faqs && page.faqs.length > 0 && (
        <section className="py-16 md:py-24 bg-[#f4f4f5]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-3xl md:text-4xl font-bold text-center uppercase tracking-tight text-[#0f0f0f] mb-12"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {page.faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-sm shadow-sm overflow-hidden">
                  <button
                    data-testid={`faq-toggle-${index}`}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-bold text-base md:text-lg text-[#0f0f0f] pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-[#b51a00] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5 text-base text-[#4a4a4a] leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Services & Areas - internal linking for SEO / crawlability */}
      <section data-testid="related-links" className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight text-[#0f0f0f] mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Explore Our Services
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RELATED_SERVICES.filter((s) => s.slug !== page.slug).map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="text-[#4a4a4a] hover:text-[#b51a00] transition-colors inline-flex items-center gap-1">
                      <ArrowRight className="w-3.5 h-3.5 text-[#b51a00]" /> {s.name}
                    </Link>
                  </li>
                ))}
                <li className="sm:col-span-2 pt-1">
                  <Link to="/services" className="text-[#b51a00] font-medium hover:underline">View all services →</Link>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight text-[#0f0f0f] mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Areas We Serve
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RELATED_AREAS.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/locations/${a.slug}`} className="text-[#4a4a4a] hover:text-[#b51a00] transition-colors inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-[#b51a00]" /> {a.name}
                    </Link>
                  </li>
                ))}
                <li className="sm:col-span-2 pt-1">
                  <Link to="/locations" className="text-[#b51a00] font-medium hover:underline">View all service areas →</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contact us today for a free, no-obligation quote. Same-day service available!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#b51a00] hover:bg-[#8f1500] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg">
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={phone_link}>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call {formatted_phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Schema.org markup for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": page.hero_title,
          "description": page.meta_description,
          "url": `https://overhaulops.com/services/${page.slug}`,
          "provider": {
            "@type": "LocalBusiness",
            "name": "Overhaul Ops LLC",
            "telephone": formatted_phone,
            "url": "https://overhaulops.com",
            "priceRange": "$$",
            "image": "https://overhaulops.com/images/overhaul-ops-logo-128.png",
            "address": { "@type": "PostalAddress", "addressRegion": "TX", "addressCountry": "US" },
            "areaServed": page.city ? [
              { "@type": "City", "name": page.city }
            ] : [
              { "@type": "AdministrativeArea", "name": "Denton County" },
              { "@type": "AdministrativeArea", "name": "Tarrant County" },
              { "@type": "AdministrativeArea", "name": "Parker County" },
              { "@type": "AdministrativeArea", "name": "Wise County" }
            ]
          },
          "areaServed": page.city
            ? { "@type": "City", "name": page.city, "containedInPlace": { "@type": "State", "name": "Texas" } }
            : { "@type": "State", "name": "Texas" },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": page.service_type_display || page.hero_title,
            "itemListElement": (page.benefits || []).map((b) => ({
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": typeof b === 'string' ? b : (b.title || b),
                "description": typeof b === 'string' ? b : (b.description || '')
              }
            }))
          },
          "keywords": (page.keywords || []).join(", ")
        })
      }} />
      </div>
    </>
  );
}
